//
// See: https://dev.to/cmuralisree/responsive-navbar-material-ui-react-router-dom-2gcd
//
import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useIsAuthenticated } from "@azure/msal-react";
import { useMediaQuery, AppBar, Toolbar, IconButton, Drawer, List, ListItem, Divider, Typography, Box } from '@mui/material';

import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import FlareIcon from '@mui/icons-material/Flare';
import ListIcon from '@mui/icons-material/List';
import QrCodeIcon from '@mui/icons-material/QrCode';
import ForestIcon from '@mui/icons-material/Forest';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import QuizIcon from '@mui/icons-material/Quiz';
import FavoriteIcon from '@mui/icons-material/Favorite';

import HideOnScroll from './HideOnScroll.js'
import NorthpowerLogo from './NorthpowerLogo.js';
import ProfileMenuItem from './ProfileMenuItem.js';
import PopUpMenu from './PopUpMenu';
import ListMenu from './ListMenu';
import { isInRole } from '../../utils/utilityFunctions.js';
import { MahiContext } from '../state/MahiContext.js';
// import { useRolesFromMahiToken } from '../../hooks/useRolesFromMahiToken.js';
// import { SignInButton } from '../auth/SignInButton';
import { SignUpSignInButton } from '../auth/SignUpSignInButton.js';

const routes = require(`../../config/routes.json`);
const env = process.env.REACT_APP_ENV;

const nestedMenuStructure = [ // Keys to the routes object imported above.
  {
    menuLabel: 'Admin',
    items:  [
      {
        itemLabel: 'List Users',
        routeKey: 'adminListUsers',
        icon:  PersonIcon,
      },
      {
        itemLabel: 'List Accounts',
        routeKey: 'adminListAccounts',
        icon:  BusinessIcon,
      },
    ],
  },
  {
    menuLabel: 'Connect',
    items:  [
      {
        itemLabel: 'Request for Information',
        routeKey: 'connectRequestForInformation',
        icon:  InfoIcon,
      },
      {
        itemLabel: 'List Inspections',
        routeKey: 'connectListIcpInspections',
        icon:  ListIcon,
      },
      {
        itemLabel: 'ICP Inspection',
        routeKey: 'icpInspection',
        icon:  FlareIcon,
      },
    ],
  },
  {
    menuLabel: 'Tags',
    items:  [
      {
        itemLabel: 'Tag Details',
        routeKey: 'tagDetails',
        icon:  QrCodeIcon,
      },
    ],
  },
  {
    menuLabel: 'Veg',
    items:  [
      {
        itemLabel: 'Tree Documents',
        routeKey: 'vegTreeDocs',
        icon:  ForestIcon,
      },
    ],
  },
  {
    menuLabel: 'Network',
    items:  [
      {
        itemLabel: 'Asset Docs',
        routeKey: 'niAssetDocs',
        icon:  PermMediaIcon,
      },
    ],
  },
  {
    menuLabel: 'DLSP',
    items:  [
      {
        itemLabel: 'All Storm Areas',
        routeKey: 'dlspListStormAreas',
        icon:  ListIcon,
      },
      {
        itemLabel: 'Desktop QA Tasks',
        routeKey: 'dlspDesktopQATaskList',
        icon:  QuizIcon,
      },
      {
        itemLabel: 'Land Owner Permission Tasks',
        routeKey: 'dlspLandOwnerPermissionTaskList',
        icon:  QuizIcon,
      },
      {
        itemLabel: 'Field QA Tasks',
        routeKey: 'dlspFieldQATaskList',
        icon:  QuizIcon,
      },
      {
        itemLabel: 'Remedial Tasks',
        routeKey: 'dlspRemedialTaskList',
        icon:  QuizIcon,
      },
      {
        itemLabel: 'Search',
        routeKey: 'dlspSearchTask',
        icon:  SearchIcon,
      },
    ],
  },
  {
    menuLabel: 'Wairua',
    items:  [
      {
        itemLabel: 'Wairua Weekly',
        routeKey: 'wairuaWeekly',
        icon:  FavoriteIcon,
      },
    ],
  },

];

const envPrimaryNonprodColor = () => { // theme is passed as parameter
  return (['production','local-prod'].includes(env)) ? 'primary.main' : 'nonprod.main'; // could get these as e.g. theme.palette.primary.main
};


export default function ResponsiveMenu() {
  const northpowerTheme = useTheme(); // from ThemeProvider
  const isMobile = useMediaQuery(northpowerTheme.breakpoints.down('md'));
  const [isOpen, setIsOpen] = useState(false)
  // console.log('Responsivemenu useContext(MahiContext)')
  // const userRoles = useRolesFromMahiToken();
  const mahiContext = useContext(MahiContext);
  const userRoles = mahiContext.userRoles;
  // console.log('ResponsiveMenu userRoles', userRoles)

  const updateIsOpen = (open) => { setIsOpen(!!open); }
  const openDrawer  = () => { updateIsOpen(true);  }
  const closeDrawer = () => { updateIsOpen(false); }

  const isAuthenticated = useIsAuthenticated();

  /**
   * 
   * @param {string} routeKey - foreign key into routes data structure, e.g. 'axos'.
   * @returns route object if route exists and user is authorised for that route, null otherwise
   *  
   */
  function getRouteIfAuth(routeKey)
  {
    if (!routes[routeKey]) {
      const msg = `Responsive Menu: Unknown route key ${routeKey}`;
      throw new Error(msg);
    }
    const routeItem = routes[routeKey];
    if (!isAuthAndRoleForMenuItem(routeItem, isAuthenticated, userRoles)) {
      //
      // User not authorised for this route
      //
      return null;
    }
    return routeItem;
  }

  function isAuthAndRoleForMenuItem(routeItem, isUserAuth, userRoles) {
    if (routeItem.requiresAuth && !isUserAuth) {
      // Menu item requires auth but user is not logged in.
      return false;
    }
    if (routeItem.requiresRole && !isInRole(userRoles, routeItem.requiresRole)) {
      // Menu item requires some role but user does not have it.
      return false;
    }
    return true;
  }

  return (
    <div style={{height: '64px'}}> {/* Space holder for elements displayed by HideOnScroll */}
      <HideOnScroll>
          <AppBar sx={{bgcolor: envPrimaryNonprodColor}}>
              <Toolbar>
                  { isMobile
                    ? <MobileMenuItems open={isOpen} onDrawerClose={closeDrawer} onDrawerOpen={openDrawer} routeAuth={getRouteIfAuth} isAuthenticated={isAuthenticated} />
                    : <DesktopMenuItems routeAuth={getRouteIfAuth} isAuthenticated={isAuthenticated} /> }
              </Toolbar>
          </AppBar>
      </HideOnScroll>
    </div>
  );
}

function DesktopMenuItems(props) {

  return (
    <>
      <Link to="/" title="Go to Homepage">
        <Box sx={{mr: 2, marginBottom: '-3px'}}>
          <NorthpowerLogo width="158px" height="55px" fill="#fff"/>
        </Box>
      </Link>
      <PopUpMenu menu={nestedMenuStructure} routeAuth={props.routeAuth} />
      <Typography variant="span" sx={{marginLeft: 'auto'}}>
        <ProfileMenuItem isAuthenticated={props.isAuthenticated}></ProfileMenuItem>
      </Typography>
    </>
  );
}

function MobileMenuItems({open, onDrawerOpen, onDrawerClose, routeAuth, isAuthenticated}) {

  return (
  <>
    <IconButton
      edge="start"
      className={isAuthenticated ? 'authenticated' : 'not-authenticated'}
      sx={{
        mr: 0,
        // [`&.authenticated`]: { bgcolor: 'gray'},
        // [`&.not-authenticated`]: { bgcolor: 'red'},
      }}
      onClick={() => onDrawerOpen()}
      color="inherit"
      size="large">
        <MenuIcon />
    </IconButton>
    {
      //
      // Put a Sign In button on the app bar when logged out. This ensures
      // we refresh the mahi context and get user roles following login on a mobile device.
      //
      isAuthenticated
      ?
      null
      :
      <SignUpSignInButton sx={{ml: 'auto'}} label="Login"></SignUpSignInButton>
    }
    <Drawer
      open={open}
      sx={{[`& .MuiDrawer-paper`]: { bgcolor: envPrimaryNonprodColor}}}
      onClick={() => {onDrawerClose()}}
    >
      <AppBar position="static">
        <Toolbar sx={{color: 'white', bgcolor: envPrimaryNonprodColor}}>
          <IconButton edge="start" sx={{mr: 0}} color="inherit" size="large">
            <MenuIcon />
          </IconButton>
          <Link to="/" title="Go to Homepage">
            <Box sx={{mr: 2, marginBottom: '-6px'}}>
              <NorthpowerLogo width="158px" height="55px" fill="#fff" />
            </Box>
          </Link>
        </Toolbar>
      </AppBar>
        <ListMenu menu={nestedMenuStructure} routeAuth={routeAuth} />
      <Divider />
      <List>
        <ListItem key="Login_Logout">
          <span sx={{margin: '0 auto'}}>
            <ProfileMenuItem isAuthenticated={isAuthenticated}></ProfileMenuItem>
          </span>
        </ListItem>
      </List>
    </Drawer>
  </>
  )
}





